import { useCallback, useRef, useState } from 'react';

import { tv, cn } from '@simplywallst/ui-core/next';

import { PrimaryListing } from '../TickerSearch/PrimaryListing';
import { SecondaryListing } from '../TickerSearch/SecondaryListing';

import type { Props as PrimaryListingProps } from '../TickerSearch/PrimaryListing';
import type { Props as SecondaryListingProps } from '../TickerSearch/SecondaryListing';

import { SearchDrawer } from '../../../SearchDrawer';

import { styles } from './styles';

const textField = tv({
  extend: styles,
});

type Option = PrimaryListingProps | SecondaryListingProps;

type Props = {
  value: string;
  label?: string;
  placeholder?: string;
  error: string | null;
  isDisabled: boolean;
  options: Option[];
  onSelect?: (id: string) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  status: 'pending' | 'success' | 'error';
};

export const MobileField = ({
  value,
  label,
  placeholder,
  error,
  status,
  onSelect,
  onInputChange,
  options,
  inputValue,
  isDisabled,
}: Props) => {
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelectionChange = useCallback(
    (keys) => {
      onSelect?.(String(...keys));
      setOpen(false);
    },
    [onSelect]
  );

  const handleInputChange = useCallback(
    (value?: string) => {
      if (value === undefined) return;
      onInputChange?.(value);
    },
    [onInputChange]
  );

  const hasError = error !== undefined && error !== '' && error !== null;

  const styles = textField({
    isDisabled,
    hasError,
  });

  const inputEl = cn([
    value.length > 0
      ? styles.inputEl({ className: 'text-left' })
      : styles.inputEl({ className: 'text-left opacity-40' }),
  ]);

  return (
    <fieldset data-invalid={hasError} className={styles.fieldset()}>
      {typeof label !== 'undefined' && (
        <label className={styles.label()}>{label}</label>
      )}
      <div className={styles.input()}>
        <button
          className={inputEl}
          disabled={isDisabled}
          onClick={() => setOpen(true)}
        >
          {value.length > 0 ? value : placeholder}
        </button>
      </div>
      {hasError && <span className={styles.error()}>{error}</span>}
      {open && (
        <div ref={containerRef}>
          <SearchDrawer
            label={label}
            placeholder={placeholder}
            items={options}
            inputValue={inputValue}
            onCancel={() => setOpen(false)}
            onSelectionChange={handleSelectionChange}
            onInputChange={handleInputChange}
            emptyStateRender={() => {
              return (
                <div className="grid justify-center">
                  <div className="mt-2 min-h-x5 px-x1_25 text-soft">
                    Couldn't find any matching items
                  </div>
                </div>
              );
            }}
            status={status}
            containerRef={containerRef}
          >
            {(item) => (
              <SearchDrawer.Option
                textValue={item.label}
                key={item.id}
                className="border-0 border-b border-solid border-subtle"
              >
                {item.type === 'primary' ? (
                  <PrimaryListing {...item} />
                ) : (
                  <SecondaryListing {...item} />
                )}
              </SearchDrawer.Option>
            )}
          </SearchDrawer>
        </div>
      )}
    </fieldset>
  );
};
